import { isNativeApp } from '@/utils/platform';

/**
 * The read side of the offline asset index.
 *
 * Every remote asset the reader wants — chapter audio, timings, token maps,
 * chapter HTML, bundle CSS, in-chapter images — is identified by an absolute
 * CDN URL by the time it reaches a call site, because `rewriteFragmentUrls`
 * has already resolved the relative ones against their chapter's URL. That
 * makes a single lookup keyed by absolute CDN URL enough to cover all of them
 * with no path arithmetic anywhere.
 *
 * This module only ever *reads*. The index is written by the asset store
 * (branch E1), which is also what decides when a file is complete enough to
 * claim. Until something writes one, `resolveAssetUrl` is the identity
 * function and the reader behaves exactly as it does today.
 */

/** Shared with the writer so both sides agree on where the index lives. */
export const OFFLINE_ASSET_INDEX_KEY = 'aleph:offline-assets:v1';

export interface OfflineAssetIndex {
  version: 1;
  /**
   * Absolute CDN URL -> local URL. Local URLs are whatever the Capacitor
   * WebView asset handler can serve; this side treats them as opaque.
   */
  entries: Record<string, string>;
}

const NO_ENTRIES: Readonly<Record<string, string>> = Object.freeze({});

// Call sites are synchronous — an <audio> src, a <link> href, a fetch() — so
// the index is parsed once and held in memory rather than re-read per lookup.
let cachedEntries: Readonly<Record<string, string>> | null = null;

const parseIndex = (raw: string): Readonly<Record<string, string>> => {
  const parsed = JSON.parse(raw) as OfflineAssetIndex;
  if (!parsed || parsed.version !== 1 || typeof parsed.entries !== 'object' || !parsed.entries) {
    return NO_ENTRIES;
  }

  // Keep only usable pairs. A half-written entry should cost that one asset a
  // fallback to the network, not poison every lookup.
  const entries: Record<string, string> = {};
  for (const [cdnUrl, localUrl] of Object.entries(parsed.entries)) {
    if (typeof cdnUrl === 'string' && cdnUrl && typeof localUrl === 'string' && localUrl) {
      entries[cdnUrl] = localUrl;
    }
  }
  return Object.freeze(entries);
};

const readEntries = (): Readonly<Record<string, string>> => {
  try {
    const raw = localStorage.getItem(OFFLINE_ASSET_INDEX_KEY);
    return raw ? parseIndex(raw) : NO_ENTRIES;
  } catch {
    // Unreadable or unparseable storage. Streaming from the network is always
    // correct, so there is nothing here worth surfacing.
    return NO_ENTRIES;
  }
};

const getEntries = (): Readonly<Record<string, string>> => {
  // Downloads are a native-app feature; the web build and the PWA never have
  // an index, so they never pay for a lookup.
  if (!isNativeApp()) {
    return NO_ENTRIES;
  }
  if (cachedEntries === null) {
    cachedEntries = readEntries();
  }
  return cachedEntries;
};

/**
 * The local URL for an asset if it is on this device, otherwise the CDN URL
 * unchanged.
 *
 * Safe to call with anything a call site might hold, including an empty string
 * or a URL that was never a bundle asset — an unknown key is returned as-is.
 */
export const resolveAssetUrl = (cdnUrl: string): string => {
  if (!cdnUrl) {
    return cdnUrl;
  }
  return getEntries()[cdnUrl] ?? cdnUrl;
};

/**
 * Drop the in-memory copy so the next lookup re-reads storage.
 *
 * The writer must call this after changing the index, otherwise a book that
 * finishes downloading mid-session keeps streaming until the next cold start.
 */
export const refreshOfflineAssetIndex = (): void => {
  cachedEntries = null;
};

/** Number of assets currently claimed as local. For diagnostics and tests. */
export const getOfflineAssetCount = (): number => Object.keys(getEntries()).length;
